import React from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { type RootState } from '@/store';
import { Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DrawerHeader } from '../components/DrawerHeader';
import { PendingPatientForm } from './PendingPatientForm';
import {
  InsertionType,
  selectEpicExtPatientIdNewPatientFormValue,
  setNewPatientInsertionType
} from '@/modules/appContext';
import { AnimatePresence, motion } from 'framer-motion';
import { DirectPatientForm } from '@/components/drawer/newPatientDrawer/DirectPatientForm';
import { TranslatedText } from '@ianesthesie-kit/core';
import dynamic from 'next/dynamic';
import { useIsCurrentHospitalEpicEnabled } from '@/modules/epic';

const ImportEpicPatientButton = dynamic(
  () =>
    import('./ImportEpicPatientButton').then(
      (mod) => mod.ImportEpicPatientButton
    ),
  { ssr: false }
);

const ContentWrapper = styled(motion.div)`
  padding: 36px 36px 0;
  min-width: 360px;
  max-width: 400px;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const selectInsertionType = (state: RootState) =>
  state.appContext.newPatientDrawer.insertionType;

export const NewPatientDrawerContent = () => {
  const insertionType = useAppSelector(selectInsertionType);
  const epicExtPatientId = useAppSelector(
    selectEpicExtPatientIdNewPatientFormValue
  );
  const epicModuleEnabled = useIsCurrentHospitalEpicEnabled();
  const dispatch = useAppDispatch();
  const setInsertionType = (type: InsertionType) =>
    dispatch(setNewPatientInsertionType(type));

  return (
    <>
      <ContentWrapper layout>
        <DrawerHeader textId="doctor-dashboard.createNewPendingPatient.drawerHeader" />
        {epicModuleEnabled && <ImportEpicPatientButton />}
        <Tabs value={insertionType} onChange={(e, v) => setInsertionType(v)}>
          <Tab
            value="pendingPatient"
            label={
              <TranslatedText id="doctor-dashboard.createNewPendingPatient.insertionType.pendingPatient" />
            }
          />
          <Tab
            value="patient"
            label={
              <TranslatedText id="doctor-dashboard.createNewPendingPatient.insertionType.patient" />
            }
          />
        </Tabs>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={insertionType}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <span key={epicExtPatientId ?? 'noepic'}>
              {insertionType === 'pendingPatient' && <PendingPatientForm />}
              {insertionType === 'patient' && <DirectPatientForm />}
            </span>
          </motion.div>
        </AnimatePresence>
      </ContentWrapper>
    </>
  );
};
